import _, {chain, intersection} from 'lodash';
import {DateTime} from 'luxon';

import {CardGroupModel} from '../models/api/cardGroupModel';
import {ChannelTypesEnum} from '../models/api/channelCommonTypes';
import {ContactSourcesEnum} from '../models/api/contactModel';
import {ConversationSortPropertiesEnum} from '../models/api/conversationCommonTypes';
import {ConversationBaseModel} from '../models/api/conversationModel';
import {
  ConversationTrackerFlagsEnum,
  ConversationTrackerModel,
  ConversationTrackerStagesEnum,
} from '../models/api/conversationTrackerModel';
import {DelegationModel} from '../models/api/delegationModel';
import {futureTimesKeyMap, FutureTimeValuesEnum} from '../models/api/futureTimeValueModel';
import {InboxModel} from '../models/api/inboxModel';
import {SelectedFilters} from '../models/api/localPreferencesModel';
import {MessageTypesEnum} from '../models/api/messageModel';
import {
  TeammateAccountStatusesEnum,
  TeammateLicenseTypesEnum,
  TeammateModel,
} from '../models/api/teammateModel';
import {TopicModel} from '../models/api/topicModel';
import {buildPublicId} from '../models/applications/publicIdTypes';
import {
  GlobalFoldersEnum,
  InboxesFiltersEnum,
  RInboxesFilter,
  TeammatesFoldersEnum,
  TeammatesMessageFoldersEnum,
  TeamsFoldersEnum,
} from '../models/url/models/inboxesUrlModel';
import {ResourceMap} from '../store/appTypes';
import {isTeamNamespace} from './models/namespaceHelpers';

/*
 * Teammate.
 */

/** Compute the namespace for the specified teammate. */
export function buildTeammateNamespace(teammateId: number) {
  return `tea:${teammateId}`;
}

/** Compute the namespace for the specified team. */
export function buildTeamNamespace(teamId: number) {
  return `tim:${teamId}`;
}

/** Compute the name to display for the specified teammate. */
export function buildTeammateDisplayName(teammate: {givenName: string; familyName: string; alias: string}) {
  const name = `${teammate.givenName} ${teammate.familyName}`.trim();
  return name || teammate.alias;
}

/** Compute the short name to display for the specified teammate. */
export function buildTeammateShortDisplayName(teammate: {
  givenName: string;
  familyName: string;
  alias: string;
}) {
  const displayName = buildTeammateDisplayName(teammate);
  return displayName.replace(/\s.*/, '');
}

/** Build a URL to the teammate invite flow in the settings. */
export function buildAddTeammateSettingsPath(namespace = 'global') {
  return `/settings/${namespace}/teammates/new`;
}

/** Checks whether or not the teammate is a visitor. */
export function isVisitorTeammate(teammate: TeammateModel) {
  return teammate.licenseType === TeammateLicenseTypesEnum.VISITOR;
}

const csatTeammateAlias = 'front_csat';
const ruleAliasPrefix = 'front_rule_';
const apiAliasPrefix = 'front_api_';

export function isBotTeammate(teammate: TeammateModel) {
  return teammate.licenseType === TeammateLicenseTypesEnum.BOT;
}

/** Checks whether or not the teammate is the csat bot. */
export function isCsatTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias === csatTeammateAlias;
}

export function isRuleTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias.startsWith(ruleAliasPrefix);
}

export function isApiTeammate(teammate: TeammateModel) {
  return isBotTeammate(teammate) && teammate.alias.startsWith(apiAliasPrefix);
}

/** Checks whether or not the teammate is active. */
export function isTeammateActive(teammate: TeammateModel) {
  return (
    teammate.accountStatus === TeammateAccountStatusesEnum.ACTIVE &&
    teammate.licenseType !== TeammateLicenseTypesEnum.VISITOR &&
    teammate.licenseType !== TeammateLicenseTypesEnum.BOT
  );
}

/** Checks whether or not the teammate is pending. */
export function isTeammatePending(teammate: TeammateModel) {
  return (
    teammate.accountStatus === TeammateAccountStatusesEnum.PENDING ||
    teammate.accountStatus === TeammateAccountStatusesEnum.PENDING_EMAIL
  );
}

/** Predicate that tests whether a teammate name contains the specified string. */
export function doesTeammateMatchQuery(teammate: TeammateModel, query: string) {
  const lowerCaseQuery = query.toLowerCase();

  // Match by email.
  const doesMatchByEmail = teammate.email.startsWith(lowerCaseQuery);

  // Match by name.
  const doesMatchByName = buildTeammateDisplayName(teammate).toLowerCase().includes(lowerCaseQuery);

  // Match by alias.
  const doesMatchByAlias = teammate.alias.toLowerCase().includes(lowerCaseQuery);

  return doesMatchByEmail || doesMatchByAlias || doesMatchByName;
}

/** Predicate that tests whether an inbox name contains the specified string. */
export function doesInboxMatchQuery(inbox: InboxModel, query: string) {
  const lowerCaseQuery = query.toLowerCase();

  // Match by name.
  const doesMatchByName = inbox.name.toLowerCase().includes(lowerCaseQuery);

  // Match by alias.
  const doesMatchByAlias = inbox.alias.toLowerCase().includes(lowerCaseQuery);

  return doesMatchByAlias || doesMatchByName;
}

/** Predicate that tests whether a card group name contains the specified string. */
export function doesCardGroupMatchQuery(group: CardGroupModel, query: string) {
  const lowerCaseQuery = query.toLowerCase();

  // Match by name.
  const doesMatchByName = group.name.toLowerCase().includes(lowerCaseQuery);

  // Match by alias.
  const doesMatchByAlias = group.alias.toLowerCase().includes(lowerCaseQuery);

  return doesMatchByName || doesMatchByAlias;
}

/** Optimized method to find if two teammates have namespaces in common */
export function hasCommonNamespace(teammate1: TeammateModel, teammate2: TeammateModel) {
  const access1 = teammate1.access;
  const access2 = teammate2.access;
  return chain(access1)
    .keys()
    .filter((namespace) => namespace !== 'global')
    .some((namespace) => {
      const namespaceAccess1 = access1[namespace];
      if (!namespaceAccess1 || namespaceAccess1.roles.length === 0) {
        return false;
      }

      const namespaceAccess2 = access2[namespace];
      return Boolean(namespaceAccess2 && namespaceAccess2.roles.length > 0);
    })
    .value();
}

/** Find the common namespaces between two teammates. */
export function findCommonNamespaces(teammate1: TeammateModel, teammate2: TeammateModel) {
  const participatingNamespaces1 = findParticipatingNamespaces(teammate1);
  const participatingNamespaces2 = findParticipatingNamespaces(teammate2);
  return intersection(participatingNamespaces1, participatingNamespaces2);
}

/** Find the namespaces in which the specified teammate has a role. */
export function findParticipatingNamespaces({access}: TeammateModel) {
  return chain(access)
    .keys()
    .filter((namespace) => {
      const namespaceAccess = access[namespace];
      return Boolean(namespaceAccess && namespaceAccess.roles.length > 0);
    })
    .value();
}

/** Check whether the provided tracker is a follower. */
export function isTrackerSubscriber(tracker?: ConversationTrackerModel) {
  if (!tracker) {
    return false;
  }

  return (
    tracker.stage === ConversationTrackerStagesEnum.ASSIGNEE ||
    tracker.stage === ConversationTrackerStagesEnum.SUBSCRIBER
  );
}

// TODO: rename to isActiveTracker in follow-up PR.
/** Check whether the provided tracker exists and currently has access to the conversation. */
export function isTracker(tracker: ConversationTrackerModel | undefined): boolean {
  return tracker ? tracker.stage !== ConversationTrackerStagesEnum.NONE : false;
}

/** Check whether a tracker has seen all the activities in a conversation. */
export function hasTrackerSeenAll(conversation: ConversationBaseModel, tracker: ConversationTrackerModel) {
  if (!tracker.autoSeenAt) {
    return false;
  }

  return (
    tracker.autoSeenAt > conversation.orders[ConversationSortPropertiesEnum.LAST_MESSAGE_OR_COMMENT_DATE]
  );
}

/** Check whether the specified conversation is unsnoozed for this tracker. */
export function isTrackerUnsnoozed(
  conversation: ConversationBaseModel,
  tracker: ConversationTrackerModel | undefined,
) {
  const trackerUnsnoozedDate = findTrackerUnsnoozedDate(tracker);
  return Boolean(
    trackerUnsnoozedDate && (!conversation.seenAt || conversation.seenAt < trackerUnsnoozedDate),
  );
}

export function findTrackerUnsnoozedDate(tracker: ConversationTrackerModel | undefined) {
  if (!tracker || !isTracker(tracker)) {
    return undefined;
  }

  if (!tracker.extra.includes(ConversationTrackerFlagsEnum.UNSNOOZED)) {
    return undefined;
  }

  return tracker.bumpedAt;
}

/** Check whether the specified tracker is an owner. */
export function isTrackerOwner(tracker: ConversationTrackerModel | undefined) {
  return tracker && isTracker(tracker) && tracker.extra.includes(ConversationTrackerFlagsEnum.OWNER);
}

/** Build an URL to a specific teammate. */
export function buildTeammatePath(
  teammateId: number,
  folder: TeammatesMessageFoldersEnum | TeammatesFoldersEnum,
  filter?: InboxesFiltersEnum,
) {
  const suffix = (filter && `/${filter}`) || '';
  return `/inboxes/teammates/${teammateId}/${folder}${suffix}`;
}

export function buildDelegationPath(delegation: DelegationModel, owner: TeammateModel) {
  if (delegation.isFull) {
    return buildTeammatePath(owner.id, TeammatesFoldersEnum.INBOX);
  }

  const firstInboxId = delegation.inboxIds[0];
  return firstInboxId ? buildTeammateInboxPath(firstInboxId) : undefined;
}

/** Find the currently selected filter for the specified teammate. */
export function findSelectedFilterForTeammate(selectedFilters: SelectedFilters, teammateId: number) {
  return (
    findSelectedInboxesFilterForKey(selectedFilters, `teammate-${teammateId}-assigned`) ||
    InboxesFiltersEnum.OPEN
  );
}

/** Checks if a teammate can access the content of an inbox. */
export function teammateCanAccessInbox(teammate: TeammateModel, inbox: InboxModel) {
  return (
    inbox.teammateIds.includes(teammate.id) ||
    (inbox.isPublic && teammateCanAccessNamespace(teammate, inbox.namespace)) ||
    // Check if teammate has access via circles.
    inbox.circleIds.some((inboxCircleId) => teammate.circleIds.includes(inboxCircleId))
  );
}

/** Checks if a teammate can access the contents of all the provided inboxes. */
export function teammateCanAccessInboxes(teammate: TeammateModel, inboxes: ReadonlyArray<InboxModel>) {
  return inboxes.every((inbox) => teammateCanAccessInbox(teammate, inbox));
}

/** Checks if a teammate can access the specified team namespace. */
export function teammateCanAccessNamespace(teammate: TeammateModel, namespace: string) {
  return Boolean(teammate.access[namespace]);
}

/** Check if a teammate can move to the given inbox. */
export function teammateCanMoveToInbox(
  user: TeammateModel,
  inbox: InboxModel,
  delegatedInboxIdsSet: Set<number>,
  canMoveToInaccessibleInbox: boolean,
) {
  // Don't allow moving to delegated inboxes.
  if (delegatedInboxIdsSet.has(inbox.id)) {
    return false;
  }

  if (canMoveToInaccessibleInbox) {
    // If we can move to inaccessible inboxes, allow the ones the teammate can access AND all team inboxes.
    return teammateCanAccessInbox(user, inbox) || isTeamInbox(inbox);
  }

  // Otherwise, just allow the ones the teammate can participate in.
  return (teammateCanAccessInbox(user, inbox) && isTeamInbox(inbox)) || inbox.namespace === user.namespace;
}

/** Returns the list of teammates allowed to access the inbox. */
export function findTeammateIdsWithInboxAccess(allTeammates: ResourceMap<TeammateModel>, inbox: InboxModel) {
  return inbox.isPublic
    ? _(allTeammates)
        .values()
        .compact()
        .filter((t) => teammateCanAccessNamespace(t, inbox.namespace))
        .map((t) => t.id)
        .value()
    : inbox.teammateIds;
}

/*
 * Team.
 */

/** Build a URL to a specific team. */
export function buildTeamPath(teamId: number, folder: TeamsFoldersEnum, filter?: InboxesFiltersEnum) {
  const basePath = `/inboxes/teams/${teamId}/${folder}`;
  return filter && folder === TeamsFoldersEnum.INBOX ? `${basePath}/${filter}` : basePath;
}

/** Find the currently selected filter for the specified team. */
export function findSelectedFilterForTeam(selectedFilters: SelectedFilters, teamId: number) {
  return findSelectedInboxesFilterForKey(selectedFilters, `team-${teamId}`) || InboxesFiltersEnum.UNASSIGNED;
}

/*
 * Global.
 */

/** Build a URL to a global folder. */
export function buildGlobalPath(folder: GlobalFoldersEnum, filter: InboxesFiltersEnum) {
  return `/inboxes/global/${folder}/${filter}`;
}

/** Find the currently selected filter for the specified global folder. */
export function findGlobalSelectedFilter(selectedFilters: SelectedFilters, folder: GlobalFoldersEnum) {
  return findSelectedInboxesFilterForKey(selectedFilters, `global-${folder}`) || InboxesFiltersEnum.OPEN;
}

/*
 * Inbox.
 */

/** Check if the specified inbox has a color. */
export function inboxHasColor(inbox: InboxModel) {
  return inbox.color.toLowerCase() !== '#ffffff';
}

/** Check whether an inbox is shared or not. */
export function isTeamInbox(inbox: InboxModel) {
  return isTeamNamespace(inbox.namespace);
}

/** Build a URL to the inbox creation flow in the settings. */
export function buildAddInboxSettingsPath(namespace: string) {
  return `/settings/${namespace}/inboxes/new`;
}

/** Build a URL to the custom view creation flow in the settings. */
export function buildAddViewSettingsPath(namespace: string) {
  return `/settings/${namespace}/custom-views/new`;
}

/** Build a URL to the custom view creation flow in the settings. */
export function buildEditViewSettingsPath(namespace: string, tagId: number) {
  return `/settings/${namespace}/custom-views/edit-by-tag/${tagId}`;
}

export function buildEditTeamInboxSettingsPath(namespace: string, inboxId: number) {
  return `/settings/${namespace}/inboxes/edit/${inboxId}`;
}

/** Build a URL to a specific team inbox. */
export function buildTeamInboxPath(inboxId: number, selectedFilter: InboxesFiltersEnum) {
  return `/inboxes/teams/folders/${inboxId}/${selectedFilter}`;
}

/** Build a URL to a specific teammate inbox. */
export function buildTeammateInboxPath(inboxId: number, filter?: InboxesFiltersEnum) {
  return `/inboxes/teammates/folders/${inboxId}${(filter && `/${filter}`) || ''}`;
}

/** Build a URL to a specific Lens. */
export function buildLensPath(lensId: number, statusFilter: InboxesFiltersEnum, isTeammateView: boolean) {
  return isTeammateView
    ? `/inboxes/teammates/saved-views/${lensId}/${statusFilter}`
    : `/inboxes/teams/saved-views/${lensId}/${statusFilter}`;
}

/** Find the currently selected filter for the specified inbox. */
export function findSelectedFilterForInbox(selectedFilters: SelectedFilters, inboxId: number) {
  const filterKey = `inbox-${inboxId}`;
  return findSelectedInboxesFilterForKey(selectedFilters, filterKey) || InboxesFiltersEnum.UNASSIGNED;
}

export function findSelectedInboxesFilterForKey(selectedFilters: SelectedFilters, key: string) {
  const selectedFilter = selectedFilters[key];
  if (!selectedFilter || !RInboxesFilter.is(selectedFilter)) {
    return undefined;
  }

  return selectedFilter;
}

const demoInboxAlias = 'demo_inbox';
const demoInboxNamePrefix = '[Sample] ';

/** Check whether this is a demo inbox or not. */
export function isDemoInbox(inbox: InboxModel) {
  if (inbox.alias === demoInboxAlias) {
    return true;
  }

  return inbox.name.startsWith(demoInboxNamePrefix);
}

/*
 * Topics.
 */

export function buildTopicPublicId(topic: TopicModel) {
  return buildPublicId('topic', topic.id);
}

/*
 * Future time.
 */

const reminderKeyRegexp = /([0-9]{1,3})(m|d|h)/;
export function futureTimeToDate(futureTime: FutureTimeValuesEnum) {
  const now = DateTime.local();

  // Two of the reminder values work differently from the rest.
  if (futureTime === FutureTimeValuesEnum.TOMORROW_9AM) {
    return now.startOf('hour').set({hour: 9}).plus({days: 1}).valueOf();
  }
  if (futureTime === FutureTimeValuesEnum.TOMORROW_2PM) {
    return now.startOf('hour').set({hour: 14}).plus({days: 1}).valueOf();
  }

  // Otherwise, we parse the reminder key.
  const reminderKey = futureTimesKeyMap[futureTime];
  if (!reminderKey) {
    throw new Error('A key for the specified reminder could not be found.');
  }

  // Extract the different parts from the key.
  const reminderKeyMatch = reminderKey.match(reminderKeyRegexp);
  if (!reminderKeyMatch) {
    throw new Error('The reminder key could not be parsed.');
  }

  const value = Number(reminderKeyMatch[1]);
  const unit = reminderKeyMatch[2];

  // Depending on the unit, add the correct amount of time.
  switch (unit) {
    case 'm':
      return now.plus({minutes: value}).valueOf();

    case 'd':
      return now.plus({days: value}).valueOf();

    default:
      throw new Error(`Invalid unit found.`);
  }
}

/*
 * Channel types -> Message type translation.
 */

const channelTypesToMessageTypesMap = {
  [ChannelTypesEnum.CUSTOM]: MessageTypesEnum.CUSTOM,
  [ChannelTypesEnum.LAYER_ANON]: MessageTypesEnum.LAYER,
  [ChannelTypesEnum.FACEBOOK]: MessageTypesEnum.FACEBOOK,
  [ChannelTypesEnum.GMAIL]: MessageTypesEnum.EMAIL,
  [ChannelTypesEnum.GOOGLE_PLAY]: MessageTypesEnum.GOOGLE_PLAY,
  [ChannelTypesEnum.IMAP]: MessageTypesEnum.EMAIL,
  [ChannelTypesEnum.INTERCOM]: MessageTypesEnum.INTERCOM,
  [ChannelTypesEnum.FORM]: MessageTypesEnum.EMAIL,
  [ChannelTypesEnum.OFFICE365]: MessageTypesEnum.EMAIL,
  [ChannelTypesEnum.SMOOCH]: MessageTypesEnum.SMOOCH,
  [ChannelTypesEnum.SMTP]: MessageTypesEnum.EMAIL,
  [ChannelTypesEnum.TALKDESK]: MessageTypesEnum.CALL,
  [ChannelTypesEnum.TRULY]: MessageTypesEnum.CALL,
  [ChannelTypesEnum.TWILIO]: MessageTypesEnum.SMS,
  [ChannelTypesEnum.TWILIO_WHATSAPP]: MessageTypesEnum.WHATSAPP,
  [ChannelTypesEnum.TWITTER]: MessageTypesEnum.TWEET,
  [ChannelTypesEnum.TWITTER_DM]: MessageTypesEnum.TWEET_DM,
  [ChannelTypesEnum.YALO_WHATSAPP]: MessageTypesEnum.YALO_WHATSAPP,
  [ChannelTypesEnum.FRONT_CHAT]: MessageTypesEnum.FRONT_CHAT,
  [ChannelTypesEnum.FRONT_MAIL]: MessageTypesEnum.EMAIL,
};

export function getMessageTypeFromChannelType(channelType: ChannelTypesEnum) {
  return channelTypesToMessageTypesMap[channelType];
}

/*
 * Message types -> Contact sources.
 */

const messageTypesToContactSourcesEnum = {
  [MessageTypesEnum.INTERNAL]: undefined,
  [MessageTypesEnum.EMAIL]: ContactSourcesEnum.EMAIL,
  [MessageTypesEnum.TWEET]: ContactSourcesEnum.TWITTER,
  [MessageTypesEnum.TWEET_DM]: ContactSourcesEnum.TWITTER_DM,
  [MessageTypesEnum.CALL]: ContactSourcesEnum.PHONE,
  [MessageTypesEnum.SMS]: ContactSourcesEnum.PHONE,
  [MessageTypesEnum.GOOGLE_PLAY]: ContactSourcesEnum.GOOGLE_PLAY,
  [MessageTypesEnum.YALO_WHATSAPP]: ContactSourcesEnum.PHONE,
  [MessageTypesEnum.WHATSAPP]: ContactSourcesEnum.PHONE,
  [MessageTypesEnum.FACEBOOK]: ContactSourcesEnum.FACEBOOK,
  [MessageTypesEnum.INTERCOM]: ContactSourcesEnum.INTERCOM,
  [MessageTypesEnum.SMOOCH]: ContactSourcesEnum.SMOOCH,
  [MessageTypesEnum.LAYER]: ContactSourcesEnum.LAYER,
  [MessageTypesEnum.CUSTOM]: ContactSourcesEnum.CUSTOM,
  [MessageTypesEnum.FRONT_CHAT]: ContactSourcesEnum.FRONT_CHAT,
};

export function getContactSourceFromMessageType(messageType: MessageTypesEnum) {
  return messageTypesToContactSourcesEnum[messageType] || ContactSourcesEnum.EMAIL;
}
